import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs';
import { environment } from '../../environments/environment';

/** The three delivery channels supported across the messaging stack. */
export type MessageChannel = 'EMAIL' | 'SMS' | 'WHATSAPP';
export const MESSAGE_CHANNELS: MessageChannel[] = ['EMAIL', 'SMS', 'WHATSAPP'];

/**
 * Provider credentials for a channel — the HOW of sending. A partner-specific
 * row (partnerId = X) overrides the global default (partnerId = null) for the
 * same channel. Only `isActive` rows are read by the senders.
 */
export interface CommunicationProviderConfigDto {
  id: number;
  partnerId: number | null; // null => Global default
  channel: MessageChannel;
  providerName: string;
  baseUrl?: string | null;
  apiKey?: string | null;
  apiSecret?: string | null;
  authToken?: string | null;
  senderId?: string | null;
  fromAddress?: string | null;
  extraConfigJson?: string | null;
  isActive: boolean;
}

/** List/detail shape — adds the joined partner name when the backend supplies it. */
export interface CommunicationProviderConfig extends CommunicationProviderConfigDto {
  partnerName?: string | null;
}

/** Responses may be PascalCase; normalize to the camelCase model. */
function mapRow(x: any): CommunicationProviderConfig {
  return {
    id: x.Id ?? x.id ?? 0,
    partnerId: x.PartnerId ?? x.partnerId ?? null,
    partnerName: x.PartnerName ?? x.partnerName ?? null,
    channel: (x.Channel ?? x.channel) as MessageChannel,
    providerName: x.ProviderName ?? x.providerName ?? '',
    baseUrl: x.BaseUrl ?? x.baseUrl ?? null,
    apiKey: x.ApiKey ?? x.apiKey ?? null,
    apiSecret: x.ApiSecret ?? x.apiSecret ?? null,
    authToken: x.AuthToken ?? x.authToken ?? null,
    senderId: x.SenderId ?? x.senderId ?? null,
    fromAddress: x.FromAddress ?? x.fromAddress ?? null,
    extraConfigJson: x.ExtraConfigJson ?? x.extraConfigJson ?? null,
    isActive: (x.IsActive ?? x.isActive ?? true) === true,
  };
}

function mapEnvelope(res: any) {
  if (Array.isArray(res?.data)) return { ...res, data: res.data.map(mapRow) };
  if (res?.data && typeof res.data === 'object') return { ...res, data: mapRow(res.data) };
  if (Array.isArray(res)) return res.map(mapRow);
  return res;
}

@Injectable({ providedIn: 'root' })
export class CommunicationConfigService {
  private http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/CommunicationConfig`;

  /** List all configs; `channel` is an optional filter. */
  getAll(channel?: MessageChannel | null) {
    const params = channel ? { channel } : undefined;
    return this.http.get(`${this.baseUrl}/GetAll`, params ? { params } : {}).pipe(map(mapEnvelope));
  }

  getById(id: number) {
    return this.http.get(`${this.baseUrl}/GetById`, { params: { id } }).pipe(map(mapEnvelope));
  }

  /** Create (id 0) or update (id > 0). */
  upsert(dto: CommunicationProviderConfigDto) {
    return this.http.post(`${this.baseUrl}/Upsert`, dto);
  }

  /** Inline active toggle — doesn't round-trip the whole form. */
  setActive(id: number, isActive: boolean) {
    return this.http.post(`${this.baseUrl}/SetActive`, null, { params: { id, isActive } });
  }

  /** Hard delete (no undo). */
  delete(id: number) {
    return this.http.get(`${this.baseUrl}/Delete`, { params: { id } });
  }
}
